import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { SOCIALS } from "@/lib/constants";
import { useSiteContent } from "@/lib/content";

export default function LatestPhotos() {
  const content = useSiteContent();
  const socials = content?.socials || SOCIALS;
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api
      .get("/albums")
      .then((r) => {
        const all = r.data.flatMap((a) => a.photos.map((p) => ({ ...p, albumName: a.name })));
        all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setPhotos(all.slice(0, 6));
      })
      .catch(() => setPhotos([]));
  }, []);

  if (photos.length === 0) return null;

  return (
    <section id="latest" data-testid="latest-photos-section" className="relative mx-auto max-w-6xl px-4 pb-28 sm:px-8">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="font-script text-2xl text-gold">Follow along</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-wine">
            The Latest from the Kitchen
          </h2>
          <p className="mt-3 max-w-lg text-sm text-plum/70">
            Fresh bakes as they come out of the oven — and even more over on Instagram.
          </p>
        </div>
        <a
          href={socials.instagram}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="latest-instagram-button"
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-burgundy/30 bg-white/80 px-6 py-3 text-sm font-semibold text-burgundy transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-golddeep"
        >
          <Instagram className="h-4 w-4 text-gold" />
          @miss_cupcake_creations_
        </a>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="latest-photos-grid">
        {photos.map((p, i) => (
          <motion.a
            key={p.id}
            href={socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="group relative aspect-square overflow-hidden rounded-2xl border border-gold/30 bg-white shadow-sm gold-glow"
            data-testid={`latest-photo-${p.id}`}
          >
            <img
              src={photoSrc(p.url)}
              alt={p.caption || `${p.albumName} creation`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-burgundy/0 opacity-0 transition-all duration-500 group-hover:bg-burgundy/45 group-hover:opacity-100">
              <Instagram className="h-7 w-7 text-white drop-shadow" />
            </span>
          </motion.a>
        ))}
      </div>
    </section>
  );
}
